/**
 * /lien-waivers — cross-project, cross-vendor lien waiver tracker.
 *
 * Shows ALL lien waivers for the entity in one view. The vendor-scoped
 * sub-tab in /vendors/$vendorId is useful when you're already looking at a
 * vendor; this page is for the controller who wants to ask:
 *   "Show me every outstanding conditional waiver we're waiting on right now."
 *
 * Toolbar: status tabs + project filter dropdown.
 * Table:   waiver # · vendor · project · type · through date · amount · status ·
 *          signed date · actions (receive / verify / void).
 *
 * Actions call:
 *   POST /api/lien-waivers/:id/receive   { signed_date }
 *   POST /api/lien-waivers/:id/verify
 *   POST /api/lien-waivers/:id/void      { reason }
 * Create calls:
 *   POST /api/lien-waivers
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ProjectIdSelect, STANDARD_PROJECT_STATUSES } from "@/components/ProjectIdSelect";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DownloadDoneIcon from "@mui/icons-material/DownloadDone";
import BlockIcon from "@mui/icons-material/Block";
import GavelIcon from "@mui/icons-material/Gavel";

import { api } from "@/lib/api";
import {
  lienWaiverKeys,
  lienWaiversListQueryOptions,
  partiesListQueryOptions,
  projectsListQueryOptions,
} from "@/lib/queries";
import { useConstructionPack } from "@/lib/useConstructionPack";
import type { LienWaiver, LienWaiverStatus, LienWaiverType, Party } from "@/lib/types";

export const Route = createFileRoute("/_auth/lien-waivers")({
  component: LienWaiversPage,
});

// ── Helpers ────────────────────────────────────────────────────────────────

const TYPE_LABEL: Record<LienWaiverType, string> = {
  conditional_partial: "Cond. Partial",
  unconditional_partial: "Uncond. Partial",
  conditional_final: "Cond. Final",
  unconditional_final: "Uncond. Final",
};

const STATUS_COLOR: Record<
  LienWaiverStatus,
  "default" | "warning" | "info" | "success" | "error"
> = {
  requested: "warning",
  received: "info",
  verified: "success",
  void: "error",
};

const STATUS_TABS: Array<{ value: LienWaiverStatus | "all"; label: string }> = [
  { value: "all", label: "All" },
  { value: "requested", label: "Requested" },
  { value: "received", label: "Received" },
  { value: "verified", label: "Verified" },
  { value: "void", label: "Void" },
];

function fmtMoney(v: string | number | null) {
  if (v == null) return "—";
  const n = Number(v);
  return isNaN(n)
    ? "—"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      }).format(n);
}

function fmtDate(s: string | null) {
  if (!s) return "—";
  return new Date(s + "T00:00:00").toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// ── Page ───────────────────────────────────────────────────────────────────

function LienWaiversPage() {
  const { enabled: constructionEnabled } = useConstructionPack();
  const qc = useQueryClient();

  const [statusTab, setStatusTab] = useState<LienWaiverStatus | "all">("all");
  const [projectFilter, setProjectFilter] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  // Action dialogs
  const [receiveTarget, setReceiveTarget] = useState<LienWaiver | null>(null);
  const [voidTarget, setVoidTarget] = useState<LienWaiver | null>(null);

  const waiversQ = useQuery(lienWaiversListQueryOptions({ project_id: projectFilter || undefined }));
  const projectsQ = useQuery(projectsListQueryOptions());
  const partiesQ = useQuery(partiesListQueryOptions({ party_type: "vendor" }));

  const partiesById = useMemo(() => {
    const m: Record<string, Party> = {};
    (partiesQ.data ?? []).forEach((p) => { m[p.id] = p; });
    return m;
  }, [partiesQ.data]);

  // Project lookup: the projects endpoint returns a paginated shape
  const projectsById = useMemo(() => {
    const m: Record<string, string> = {};
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const raw = (projectsQ.data as any)?.results ?? projectsQ.data ?? [];
    const rows: any[] = Array.isArray(raw) ? raw : [];
    rows.forEach((p) => { m[p.id] = p.code ? `${p.code} – ${p.name}` : p.name; });
    return m;
  }, [projectsQ.data]);

  const waivers = useMemo(() => {
    const all = waiversQ.data ?? [];
    if (statusTab === "all") return all;
    return all.filter((w) => w.status === statusTab);
  }, [waiversQ.data, statusTab]);

  // Status counts for tab badges
  const counts = useMemo(() => {
    const all = waiversQ.data ?? [];
    const c: Record<string, number> = { all: all.length };
    all.forEach((w) => { c[w.status] = (c[w.status] ?? 0) + 1; });
    return c;
  }, [waiversQ.data]);

  const verifyMut = useMutation({
    mutationFn: (id: string) =>
      api<LienWaiver>(`/api/lien-waivers/${id}/verify`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: lienWaiverKeys.all }),
  });

  if (!constructionEnabled) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-500 dark:text-slate-400">
        The Construction pack is not enabled for this entity.
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Toolbar */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800 flex items-center gap-4 flex-wrap">
        <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100 shrink-0">
          Lien Waivers
        </h1>
        <span className="text-xs text-slate-500 dark:text-slate-400 shrink-0">
          {waiversQ.data?.length ?? 0} total
        </span>

        <Box sx={{ minWidth: 280 }}>
          <ProjectIdSelect
            value={projectFilter}
            onChange={setProjectFilter}
            statusFilter={STANDARD_PROJECT_STATUSES}
            label="Project"
            placeholder="All projects…"
          />
        </Box>

        <div className="flex-1" />
        <Button
          component={Link}
          to="/lien-waivers/templates"
          variant="outlined"
          startIcon={<GavelIcon />}
          size="small"
        >
          State Library
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          size="small"
        >
          New Waiver
        </Button>
      </div>

      {/* Status tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "background.paper" }}>
        <Tabs
          value={statusTab}
          onChange={(_, v) => setStatusTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ minHeight: 40, "& .MuiTab-root": { minHeight: 40, py: 0, fontSize: 13 } }}
        >
          {STATUS_TABS.map(({ value, label }) => (
            <Tab
              key={value}
              value={value}
              label={
                <span>
                  {label}
                  {counts[value] != null && (
                    <span className="ml-1.5 rounded-full bg-slate-100 dark:bg-gray-700 text-slate-600 dark:text-slate-300 text-[10px] px-1.5 py-0.5 font-medium">
                      {counts[value]}
                    </span>
                  )}
                </span>
              }
            />
          ))}
        </Tabs>
      </Box>

      {/* Table */}
      <div className="flex-1 overflow-auto px-6 py-4">
        {waiversQ.isLoading && <CircularProgress size={24} />}
        {waiversQ.isError && (
          <Alert severity="error">
            Failed to load lien waivers: {(waiversQ.error as Error)?.message}
          </Alert>
        )}

        {!waiversQ.isLoading && waivers.length === 0 && (
          <Alert severity="info">
            {statusTab === "all"
              ? "No lien waivers yet. Create one to start tracking."
              : `No waivers with status "${statusTab}".`}
          </Alert>
        )}

        {waivers.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Waiver #</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Through</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Signed</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {waivers.map((w) => (
                <TableRow key={w.id} hover>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                    {w.waiver_no}
                  </TableCell>
                  <TableCell>
                    {partiesById[w.vendor_id]?.name ?? (
                      <span className="text-slate-400 font-mono text-xs">
                        {w.vendor_id.slice(0, 8)}…
                      </span>
                    )}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {projectsById[w.project_id] ?? (
                      <span className="text-slate-400 font-mono text-xs">
                        {w.project_id.slice(0, 8)}…
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="text-xs text-slate-600 dark:text-slate-400">
                      {TYPE_LABEL[w.waiver_type]}
                    </span>
                  </TableCell>
                  <TableCell>{fmtDate(w.through_date)}</TableCell>
                  <TableCell align="right">{fmtMoney(w.amount_through)}</TableCell>
                  <TableCell>
                    <Chip
                      label={w.status}
                      size="small"
                      color={STATUS_COLOR[w.status]}
                      variant={w.status === "verified" ? "filled" : "outlined"}
                    />
                  </TableCell>
                  <TableCell>{fmtDate(w.signed_date)}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" justifyContent="flex-end" gap={0.5}>
                      {w.status === "requested" && (
                        <Tooltip title="Mark received">
                          <IconButton
                            size="small"
                            onClick={() => setReceiveTarget(w)}
                          >
                            <DownloadDoneIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {w.status === "received" && (
                        <Tooltip title="Verify">
                          <IconButton
                            size="small"
                            disabled={verifyMut.isPending}
                            onClick={() => verifyMut.mutate(w.id)}
                          >
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {(w.status === "requested" || w.status === "received") && (
                        <Tooltip title="Void">
                          <IconButton
                            size="small"
                            onClick={() => setVoidTarget(w)}
                          >
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Dialogs */}
      <CreateWaiverDialog
        open={createOpen}
        partiesById={partiesById}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          qc.invalidateQueries({ queryKey: lienWaiverKeys.all });
          setCreateOpen(false);
        }}
      />

      <ReceiveDialog
        waiver={receiveTarget}
        onClose={() => setReceiveTarget(null)}
        onReceived={() => {
          qc.invalidateQueries({ queryKey: lienWaiverKeys.all });
          setReceiveTarget(null);
        }}
      />

      <VoidDialog
        waiver={voidTarget}
        onClose={() => setVoidTarget(null)}
        onVoided={() => {
          qc.invalidateQueries({ queryKey: lienWaiverKeys.all });
          setVoidTarget(null);
        }}
      />
    </div>
  );
}

// ── Create dialog ──────────────────────────────────────────────────────────

function CreateWaiverDialog({
  open,
  partiesById,
  onClose,
  onCreated,
}: {
  open: boolean;
  partiesById: Record<string, Party>;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [waiverNo, setWaiverNo] = useState("");
  const [projectId, setProjectId] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [waiverType, setWaiverType] = useState<LienWaiverType>("conditional_partial");
  const [throughDate, setThroughDate] = useState("");
  const [amount, setAmount] = useState("0");
  const [notes, setNotes] = useState("");

  const saveMut = useMutation({
    mutationFn: () =>
      api<LienWaiver>("/api/lien-waivers", {
        method: "POST",
        body: JSON.stringify({
          waiver_no: waiverNo,
          project_id: projectId,
          vendor_id: vendorId,
          waiver_type: waiverType,
          through_date: throughDate,
          amount_through: amount,
          notes: notes || null,
        }),
        headers: { "Content-Type": "application/json" },
      }),
    onSuccess: onCreated,
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New Lien Waiver</DialogTitle>
      <DialogContent>
        <Stack gap={2} mt={1}>
          <TextField
            label="Waiver number"
            required
            fullWidth
            value={waiverNo}
            onChange={(e) => setWaiverNo(e.target.value)}
            placeholder="e.g. LW-2024-001"
          />
          <ProjectIdSelect
            value={projectId}
            onChange={setProjectId}
            statusFilter={STANDARD_PROJECT_STATUSES}
            label="Project"
            required
            placeholder="Search project code or name…"
          />
          <FormControl fullWidth required>
            <InputLabel>Vendor</InputLabel>
            <Select value={vendorId} label="Vendor" onChange={(e) => setVendorId(e.target.value)}>
              {Object.values(partiesById).map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth required>
            <InputLabel>Waiver type</InputLabel>
            <Select
              value={waiverType}
              label="Waiver type"
              onChange={(e) => setWaiverType(e.target.value as LienWaiverType)}
            >
              {(Object.keys(TYPE_LABEL) as LienWaiverType[]).map((t) => (
                <MenuItem key={t} value={t}>{TYPE_LABEL[t]}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack direction="row" gap={2}>
            <TextField
              label="Through date"
              type="date"
              required
              fullWidth
              value={throughDate}
              onChange={(e) => setThroughDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Amount through"
              required
              fullWidth
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputProps={{ inputMode: "decimal" }}
            />
          </Stack>
          <TextField
            label="Notes"
            fullWidth
            multiline
            minRows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          {saveMut.isError && (
            <Alert severity="error">
              {(saveMut.error as Error)?.message ?? "Failed to create waiver"}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!waiverNo || !projectId || !vendorId || !throughDate || saveMut.isPending}
          onClick={() => saveMut.mutate()}
        >
          {saveMut.isPending ? "Creating…" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Receive dialog ─────────────────────────────────────────────────────────

function ReceiveDialog({
  waiver,
  onClose,
  onReceived,
}: {
  waiver: LienWaiver | null;
  onClose: () => void;
  onReceived: () => void;
}) {
  const [signedDate, setSignedDate] = useState(
    new Date().toISOString().slice(0, 10),
  );

  const receiveMut = useMutation({
    mutationFn: () =>
      api<LienWaiver>(`/api/lien-waivers/${waiver!.id}/receive`, {
        method: "POST",
        body: JSON.stringify({ signed_date: signedDate }),
        headers: { "Content-Type": "application/json" },
      }),
    onSuccess: onReceived,
  });

  return (
    <Dialog open={!!waiver} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Mark Received — {waiver?.waiver_no}</DialogTitle>
      <DialogContent>
        <Stack gap={2} mt={1}>
          <TextField
            label="Signed date"
            type="date"
            required
            fullWidth
            value={signedDate}
            onChange={(e) => setSignedDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          {receiveMut.isError && (
            <Alert severity="error">
              {(receiveMut.error as Error)?.message ?? "Failed to receive waiver"}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!signedDate || receiveMut.isPending}
          onClick={() => receiveMut.mutate()}
        >
          {receiveMut.isPending ? "Saving…" : "Mark received"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Void dialog ────────────────────────────────────────────────────────────

function VoidDialog({
  waiver,
  onClose,
  onVoided,
}: {
  waiver: LienWaiver | null;
  onClose: () => void;
  onVoided: () => void;
}) {
  const [reason, setReason] = useState("");

  const voidMut = useMutation({
    mutationFn: () =>
      api<LienWaiver>(`/api/lien-waivers/${waiver!.id}/void`, {
        method: "POST",
        body: JSON.stringify({ reason }),
        headers: { "Content-Type": "application/json" },
      }),
    onSuccess: onVoided,
  });

  return (
    <Dialog open={!!waiver} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Void Waiver — {waiver?.waiver_no}</DialogTitle>
      <DialogContent>
        <Stack gap={2} mt={1}>
          <Typography variant="body2" color="text.secondary">
            Voiding is permanent and cannot be undone. Provide a reason for the audit trail.
          </Typography>
          <TextField
            label="Reason"
            required
            fullWidth
            multiline
            minRows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Duplicate, wrong period, replaced by LW-2024-002"
          />
          {voidMut.isError && (
            <Alert severity="error">
              {(voidMut.error as Error)?.message ?? "Failed to void waiver"}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          disabled={!reason.trim() || voidMut.isPending}
          onClick={() => voidMut.mutate()}
        >
          {voidMut.isPending ? "Voiding…" : "Void waiver"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
